import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { message, Button, Tooltip, Spin, Input, Modal } from 'antd';
import ZoomVideo from '@zoom/videosdk';
import {
  getDetailSession,
  getTokenSession,
  verifyJoinSession,
  webAcquireZoomLock,
  webUpdateSessionStatus,
  moderateText,
  logSensitiveKeywordEvent,
} from 'redux/actions/mvp/session.action';
import { fetchUserMvp } from 'redux/actions/user.action';
import { I18n } from '../../../../helpers/i18n';
import sensitiveKeywordsConfig from '../../../../config/sensitiveKeywords.json';
import './session-video-custom.scss';
import {
  findSameCedUserParticipants,
  getParticipantCedUserId,
  hasSameUserWithMicOn,
  normalizeParticipantEventPayload,
  participantUserIdsMatch,
  resolveMyCedUserId,
  withSelfMutedWhenMicOff,
} from './sessionVideoParticipantUtils';
import {
  clearPersistedParticipantToken,
  loadPersistedParticipantToken,
  persistParticipantToken,
  releaseParticipantLockKeepalive,
} from '../../../../helpers/mvpZoomParticipantLock';
import {
  clearPrejoinMediaPrefs,
  loadPrejoinMediaPrefs,
} from '../../../../constants/mvpDeviceCombos';
import {
  createSessionVideoMediaRegistry,
  findSharerFromUsers,
  getParticipantIdsKey,
  getParticipantVideoFingerprint,
  shouldUpdateParticipantsList,
} from './sessionVideoMediaRegistry';
import { applyPiPStashState, parsePiPStashCommand } from './sessionVideoPiPStash';
import SessionVideoUserBox from './session-video-user-box';

const MAX_PINNED_DEVICES = 2;
/** When screen share is sticky on main, only one device pin may sit beside it. */
const MAX_PINNED_WITH_SHARE = 1;
const MAIN_RESOLUTION = { width: 640, height: 360 };
const SIDEBAR_RESOLUTION = { width: 320, height: 180 };
const SHARE_SLOT_POLL_MAX_FRAMES = 30;
/** Shorter poll when sync slot check fails — avoid losing user-gesture before startShareScreen. */
const SHARE_SLOT_POLL_FALLBACK_FRAMES = 5;

function nextAnimationFrame() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 16);
    }
  });
}

/** Canvas/video đã nằm trong sticky main share slot và có kích thước > 0. */
export function isShareSlotReady(target, mainShareSlotEl) {
  if (!target || !mainShareSlotEl) return false;
  if (target.parentNode !== mainShareSlotEl) return false;
  const rect = mainShareSlotEl.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

export async function waitForShareSlotReady(
  target,
  mainShareSlotEl,
  maxFrames = SHARE_SLOT_POLL_MAX_FRAMES,
) {
  for (let i = 0; i < maxFrames; i += 1) {
    if (isShareSlotReady(target, mainShareSlotEl)) {
      return true;
    }
    await nextAnimationFrame();
  }
  return isShareSlotReady(target, mainShareSlotEl);
}

/**
 * Kích thước tối đa của rolling buffer gửi cho L2 (đồng bộ kL2TextTailMax bản macOS).
 * Zoom cắt caption theo từng câu nói, nên L2 cần nhiều lượt hội thoại mới đủ ngữ cảnh
 * để phân biệt "hỏi số tài khoản để chuyển tiền" với "bàn về tài khoản ngân hàng nói chung".
 */
const L2_TEXT_TAIL_MAX = 2000;
/** Khoảng cách tối thiểu giữa 2 lần gọi L2 (đồng bộ kL2MinRequestInterval bản macOS). */
const L2_MIN_REQUEST_INTERVAL_MS = 3000;
/**
 * Cooldown sau một cảnh báo, tính từ lúc user đóng modal và quay lại phiên. Độ dài phụ thuộc
 * chính lựa chọn của user — họ là người biết rõ nhất đoạn hội thoại vừa rồi thực sự an toàn
 * tới đâu:
 *  - "An toàn, tiếp tục": user khẳng định cảnh báo là báo động giả → im lặng lâu hơn.
 *  - "Tiếp tục, chú ý hơn": user thừa nhận có rủi ro thật → cảnh giác trở lại sớm hơn gấp ba.
 */
const ALERT_COOLDOWN_SAFE_MS = 45000;
const ALERT_COOLDOWN_CAREFUL_MS = 15000;
/** Dùng khi chưa có lựa chọn nào (lúc modal vừa mở) — lấy mức thận trọng nhất. */
const ALERT_COOLDOWN_DEFAULT_MS = ALERT_COOLDOWN_SAFE_MS;
/** Số câu tối đa giữ trong buffer — chặn mảng phình vô hạn trong phiên dài. */
const MAX_TRANSCRIPT_SEGMENTS = 40;

/**
 * Zoom Live Transcription bắn caption TẠM THỜI: cùng một câu được gửi lại nhiều lần, mỗi lần
 * dài thêm ("Hi, I just" → "Hi, I just got a text with" → "Hi, I just got a text with a 6-").
 * Nối thẳng từng lần vào buffer sẽ nhân bản nội dung theo cấp số nhân và đẩy ngữ cảnh thật
 * ra khỏi cửa sổ L2_TEXT_TAIL_MAX.
 *
 * Dedupe theo `msgId` (ID ổn định của một câu, theo LiveTranscriptionMessage của SDK). Khi SDK
 * không gửi msgId thì nhận diện bằng quan hệ tiền tố với câu ngay trước.
 */
function upsertTranscriptSegment(segments, line, msgId) {
  if (msgId) {
    const existing = segments.find((s) => s.msgId === msgId);
    if (existing) {
      existing.text = line;
      return segments;
    }
  } else {
    const last = segments[segments.length - 1];
    if (last && !last.msgId && (line.startsWith(last.text) || last.text.startsWith(line))) {
      // Cùng một câu đang được đọc dở — giữ bản dài hơn thay vì thêm dòng mới.
      if (line.length >= last.text.length) last.text = line;
      return segments;
    }
  }
  segments.push({ msgId: msgId || null, text: line });
  if (segments.length > MAX_TRANSCRIPT_SEGMENTS) {
    segments.splice(0, segments.length - MAX_TRANSCRIPT_SEGMENTS);
  }
  return segments;
}

/** Ghép các câu đã dedupe thành payload gửi L2, giữ phần đuôi tối đa L2_TEXT_TAIL_MAX ký tự. */
function buildRollingText(segments) {
  const text = segments.map((s) => s.text).join('\n');
  return text.length > L2_TEXT_TAIL_MAX ? text.slice(text.length - L2_TEXT_TAIL_MAX) : text;
}

/** Kiểm tra transcript có chứa từ khóa trong danh sách JSON không. Trả về { detected, category, keyword } hoặc { detected: false }. */
function detectSensitiveByJson(text) {
  if (!text || !sensitiveKeywordsConfig.categories || !Array.isArray(sensitiveKeywordsConfig.categories)) {
    return { detected: false };
  }
  const lower = text.toLowerCase().trim();
  for (const cat of sensitiveKeywordsConfig.categories) {
    const keywords = cat.keywords || [];
    for (const kw of keywords) {
      if (!kw || typeof kw !== 'string') continue;
      if (lower.includes(kw.toLowerCase())) {
        return { detected: true, category: cat.name, keyword: kw };
      }
    }
  }
  return { detected: false };
}

/** participantToken từ body API (lock / join-session): nhiều envelope (data, snake_case). */
function pickParticipantTokenFromFlatObject(obj) {
  if (!obj || typeof obj !== 'object') return '';
  const raw =
    obj.participantToken ??
    obj.participant_token ??
    obj.ParticipantToken ??
    obj.W1 ??
    obj.w1 ??
    (obj.w1 && typeof obj.w1 === 'object'
      ? obj.w1.participantToken ?? obj.w1.participant_token
      : undefined);
  return raw != null && String(raw).trim() ? String(raw).trim() : '';
}

function pickParticipantToken(payload) {
  if (!payload || typeof payload !== 'object') return '';
  let out = pickParticipantTokenFromFlatObject(payload);
  if (out) return out;
  if (payload.data != null && typeof payload.data === 'object') {
    out = pickParticipantTokenFromFlatObject(payload.data);
    if (out) return out;
  }
  if (payload.Data != null && typeof payload.Data === 'object') {
    out = pickParticipantTokenFromFlatObject(payload.Data);
    if (out) return out;
  }
  return '';
}

/**
 * Tên hiển thị khi Zoom join. Sau full-page reload, Redux user thường chưa hydrate
 * lúc mount effect chạy join — đọc thêm `ced:user-info` (cùng nguồn auth MVP).
 */
function resolveZoomDisplayName(userFromRedux) {
  const fromRedux = userFromRedux?.name != null ? String(userFromRedux.name).trim() : '';
  if (fromRedux) return fromRedux;
  try {
    const raw = localStorage.getItem('ced:user-info');
    if (raw) {
      const parsed = JSON.parse(raw);
      const fromStorage = parsed?.name != null ? String(parsed.name).trim() : '';
      if (fromStorage) return fromStorage;
    }
  } catch (_) {
    /* ignore */
  }
  return '';
}

/** Body 200 của mvp/web-update-session-status — backend có thể không trả đúng status: "OK". */
function isWebUpdateSessionStatusSuccess(res) {
  if (res == null) return true;
  if (typeof res !== 'object' || Array.isArray(res)) return true;
  const st = res.status;
  if (typeof st === 'string') {
    const u = st.toUpperCase();
    if (u === 'ERROR' || u === 'FAIL' || u === 'FAILED') return false;
    if (u === 'OK' || u === 'SUCCESS' || u === 'SUCCEEDED') return true;
  }
  if (res.success === false) return false;
  if (res.success === true) return true;
  if (res.data != null && typeof res.data === 'object') {
    const d = res.data;
    // Chuẩn thành công: { status: "OK", data: { updated: true, sessionStatus: "joined" } }
    if (
      d.updated === true &&
      typeof d.sessionStatus === 'string' &&
      d.sessionStatus.toLowerCase() === 'joined'
    ) {
      return true;
    }
    const inner = res.data.status;
    if (typeof inner === 'string') {
      const u = String(inner).toUpperCase();
      if (u === 'ERROR' || u === 'FAIL' || u === 'FAILED') return false;
      if (u === 'OK' || u === 'SUCCESS') return true;
    }
  }
  const code = res.statusCode ?? res.code;
  if (typeof code === 'number' && code >= 400) return false;
  return true;
}

/** Bật lại (true) để xem console debug log của Live Transcript / từ khóa nhạy cảm. */
const DEBUG_SENSITIVE_KEYWORD = false;

import {
  AudioMutedOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  VideoCameraAddOutlined,
  ShareAltOutlined,
  StopOutlined,
  MessageOutlined,
  PhoneOutlined,
  MoreOutlined,
  LoadingOutlined,
  UserOutlined,
  CloseOutlined,
  SendOutlined,
} from '@ant-design/icons';

function SessionVideoCustom({
  history,
  match,
  user,
  getDetailSession,
  getTokenSession,
  verifyJoinSession,
  webAcquireZoomLock,
  webUpdateSessionStatus,
  moderateText: moderateTextAction,
  logSensitiveKeywordEvent: logSensitiveKeywordEventAction,
}) {
  const sessionId = match.params.id;
  const initialPrejoinPrefs = loadPrejoinMediaPrefs(sessionId);
  const [client, setClient] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(
    initialPrejoinPrefs ? initialPrejoinPrefs.audio !== false : true,
  );
  const [isVideoEnabled, setIsVideoEnabled] = useState(
    initialPrejoinPrefs ? initialPrejoinPrefs.video !== false : true,
  );
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const userVideoRefs = useRef({});
  const shareCanvasRef = useRef(null);
  const shareVideoRef = useRef(null);
  const shareHostRef = useRef(null);
  const mainShareSlotRef = useRef(null);
  const [shareRenderType, setShareRenderType] = useState('canvas'); // 'canvas' | 'video'
  const [activeShareUser, setActiveShareUser] = useState(null);
  const [pinnedDevices, setPinnedDevices] = useState([]);
  const [railFocusedUserId, setRailFocusedUserId] = useState(null);
  const [showChat, setShowChat] = useState(false); // Control chat panel visibility
  const [chatMessages, setChatMessages] = useState([]); // Zoom chat messages
  const [chatInput, setChatInput] = useState('');
  const [chatClient, setChatClient] = useState(null);
  const [chatToast, setChatToast] = useState(null); // { sender, text }
  /** Modal cảnh báo từ khóa nhạy cảm: { keyword, category, speaker, transcript, source } khi mở, null khi đóng */
  const [sensitiveModal, setSensitiveModal] = useState(null);
  const [sensitiveModalSubmitting, setSensitiveModalSubmitting] = useState(false);
  /** Flow dừng cuộc gọi: sau khi chọn "Dừng cuộc gọi" mở modal nhập feedback, lưu data để gọi API khi confirm */
  const [endSessionFeedbackVisible, setEndSessionFeedbackVisible] = useState(false);
  const [endSessionFeedbackNote, setEndSessionFeedbackNote] = useState('');
  const pendingEndSessionRef = useRef(null);

  const pinnedDevicesRef = useRef([]);
  const activeShareUserRef = useRef(null);
  const chatToastTimeoutRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const sessionUserIdRef = useRef(null);
  const sensitiveCheckInFlightRef = useRef(false);
  /** Ngữ cảnh hội thoại tích lũy (tối đa L2_TEXT_TAIL_MAX ký tự) gửi cho L2 thay vì 1 dòng caption lẻ. */
  const rollingSegmentsRef = useRef([]);
  /** Thời điểm gọi L2 gần nhất — dùng cho debounce. */
  const lastL2RequestAtRef = useRef(0);
  /** Mốc bắt đầu cooldown: lúc modal cảnh báo đóng lại (fallback là lúc modal mở). */
  const lastAlertAtRef = useRef(0);
  /** Độ dài cooldown đang áp dụng — do lựa chọn của user trên modal quyết định. */
  const alertCooldownMsRef = useRef(ALERT_COOLDOWN_DEFAULT_MS);
  /** Timer flush buffer khi lần check bị hoãn vì debounce/inFlight (tránh mất caption cuối). */
  const pendingL2TimerRef = useRef(null);
  /** Kết quả L1 đang chờ L2 xác nhận — dùng làm fallback khi L2 không trả lời được. */
  const pendingL1MatchRef = useRef(null);
  /** Đọc trạng thái modal trong callback caption mà không cần re-register listener. */
  const sensitiveModalOpenRef = useRef(false);
  /** Tránh join nhiều lần (Strict Mode / re-mount) và cleanup khi unmount */
  const joiningInProgressRef = useRef(false);
  const mountedRef = useRef(true);
  const clientRef = useRef(null);
  /** Heartbeat POST mvp/web-update-session-status (60s) sau khi vào room thành công */
  const sessionStatusHeartbeatIntervalRef = useRef(null);
  const participantTokenHeartbeatRef = useRef('');
  /** Refs để đọc trạng thái mic/cam trong callback (caption) — luôn sync với state */
  const isAudioEnabledRef = useRef(initialPrejoinPrefs ? initialPrejoinPrefs.audio !== false : true);
  const isVideoEnabledRef = useRef(initialPrejoinPrefs ? initialPrejoinPrefs.video !== false : true);
  const prejoinVideoDeviceIdRef = useRef(initialPrejoinPrefs?.videoDeviceId || '');
  /** Audio đang join speakerOnly (chưa nối micro) — bật mic phải stopAudio + startAudio lại. */
  const audioSpeakerOnlyRef = useRef(false);
  /** Một toast mỗi lần xung đột mic; reset khi peer rời hoặc unmute thành công. */
  const hasShownMultiDeviceMicToastRef = useRef(false);
  /**
   * Peer cùng user đã mic ON lúc web join (web là người vào sau) — không yield vì các id này.
   * Peer join/unmute sau không nằm trong set → web phải tắt mic.
   */
  const preExistingSameUserMicOnIdsRef = useRef(new Set());
  /** Trạng thái mic/cam trước khi mở modal kiểm duyệt — dùng để khôi phục khi đóng modal */
  const mediaStateBeforeSensitiveModalRef = useRef(null);
  const allUsersRef = useRef([]);
  const mainUserIdsRef = useRef(new Set());
  const mediaRegistryRef = useRef(createSessionVideoMediaRegistry());
  /** Zoom user id của máy iOS đang thu PiP vào cạnh màn hình (camera bị đứng hình). */
  const pipStashedUserIdsRef = useRef(new Set());
  /** Local share: click handler set flag; useLayoutEffect gọi SDK sau khi DOM slot sẵn sàng. */
  const pendingLocalShareRef = useRef(false);
  /** Rejoin while already sharing: only startShareView needed after layout. */
  const pendingLocalShareViewRef = useRef(false);
  /** Tránh gọi startShareScreen lần 2 khi useLayoutEffect re-run. */
  const localShareInFlightRef = useRef(false);

  useEffect(() => {
    pinnedDevicesRef.current = pinnedDevices;
  }, [pinnedDevices]);

  useEffect(() => {
    activeShareUserRef.current = activeShareUser;
  }, [activeShareUser]);

  useEffect(() => {
    allUsersRef.current = allUsers;
  }, [allUsers]);

  const effectiveShareUser = useMemo(
    () => allUsers.find((u) => u.sharerOn) || activeShareUser || null,
    [allUsers, activeShareUser],
  );

  const videoFingerprint = useMemo(() => getParticipantVideoFingerprint(allUsers), [allUsers]);

  const participantIdsKey = useMemo(() => getParticipantIdsKey(allUsers), [allUsers]);

  const pinnedLayout = useMemo(() => {
    const activePinnedDevices = pinnedDevices
      .map((p) => allUsers.find((u) => participantUserIdsMatch(u.userId, p.userId)))
      .filter(Boolean);
    return {
      activePinnedDevices,
      pinnedUserIds: new Set(activePinnedDevices.map((p) => String(p.userId))),
      pinnedDeviceKey: activePinnedDevices.map((p) => p.userId).join(','),
      hasPin: activePinnedDevices.length > 0,
    };
  }, [pinnedDevices, allUsers]);

  const { activePinnedDevices, pinnedUserIds, pinnedDeviceKey, hasPin } = pinnedLayout;
  const stickyShareOnMain = !!effectiveShareUser;

  const mainLayout = useMemo(() => {
    let mainUsers = [];
    if (stickyShareOnMain) {
      // Share occupies its own sticky pane; only pinned devices sit beside it (max 1).
      mainUsers = activePinnedDevices.slice(0, MAX_PINNED_WITH_SHARE);
    } else if (activePinnedDevices.length > 0) {
      mainUsers = activePinnedDevices;
    } else {
      const focusedUser = railFocusedUserId
        ? allUsers.find((u) => participantUserIdsMatch(u.userId, railFocusedUserId))
        : null;
      if (focusedUser) {
        mainUsers = [focusedUser];
      } else {
        const cameraUser = allUsers.find((u) => !!(u.isVideoOn || u.bVideoOn));
        if (cameraUser) {
          mainUsers = [cameraUser];
        } else {
          const selfZoomId = client?.getCurrentUserInfo()?.userId;
          const selfUser = selfZoomId
            ? allUsers.find((u) => participantUserIdsMatch(u.userId, selfZoomId))
            : null;
          mainUsers = selfUser ? [selfUser] : [];
        }
      }
    }
    const mainUserIds = new Set(mainUsers.map((u) => String(u.userId)));
    return {
      mainUsers,
      mainUserIds,
      mainDeviceKey: mainUsers.map((u) => u.userId).join(','),
      mainContent: stickyShareOnMain || mainUsers.length > 0,
    };
  }, [activePinnedDevices, allUsers, client, stickyShareOnMain, railFocusedUserId]);

  const { mainUsers, mainUserIds, mainDeviceKey, mainContent } = mainLayout;

  useEffect(() => {
    mainUserIdsRef.current = mainUserIds;
  }, [mainUserIds]);

  // Clear rail focus when share owns main. Remote share trims pins here;
  // local share already trims in handleStartShare (same click tick) — skip while
  // pendingLocalShareRef so we don't touch state while layout prepares the slot.
  useEffect(() => {
    if (!stickyShareOnMain) return;
    setRailFocusedUserId(null);
    if (pendingLocalShareRef.current) return;
    setPinnedDevices((prev) => {
      if (prev.length <= MAX_PINNED_WITH_SHARE) return prev;
      return prev.slice(0, MAX_PINNED_WITH_SHARE);
    });
  }, [stickyShareOnMain]);

  useEffect(() => {
    setPinnedDevices((prev) => {
      if (prev.length === 0) return prev;
      const activeIds = new Set(participantIdsKey.split('|').filter(Boolean));
      const next = prev.filter((p) => activeIds.has(String(p.userId)));
      return next.length === prev.length ? prev : next;
    });
  }, [participantIdsKey]);

  useEffect(() => {
    setRailFocusedUserId((prev) => {
      if (!prev) return prev;
      const activeIds = new Set(participantIdsKey.split('|').filter(Boolean));
      return activeIds.has(String(prev)) ? prev : null;
    });
  }, [participantIdsKey]);

  const assignUserVideoRef = useCallback((userId, el) => {
    if (el) {
      userVideoRefs.current[userId] = el;
    } else {
      delete userVideoRefs.current[userId];
      mediaRegistryRef.current.clearVideoRegistry(userId);
    }
  }, []);

  const getVideoResolutionForUser = useCallback((userId) => {
    if (mainUserIdsRef.current.has(String(userId))) {
      return MAIN_RESOLUTION;
    }
    return SIDEBAR_RESOLUTION;
  }, []);

  useEffect(() => {
    isAudioEnabledRef.current = isAudioEnabled;
    isVideoEnabledRef.current = isVideoEnabled;
  }, [isAudioEnabled, isVideoEnabled]);

  useEffect(() => {
    sensitiveModalOpenRef.current = !!sensitiveModal;
  }, [sensitiveModal]);

  useEffect(
    () => () => {
      if (pendingL2TimerRef.current) {
        clearTimeout(pendingL2TimerRef.current);
        pendingL2TimerRef.current = null;
      }
    },
    []
  );

  const clearSessionStatusHeartbeat = useCallback(() => {
    if (sessionStatusHeartbeatIntervalRef.current != null) {
      clearInterval(sessionStatusHeartbeatIntervalRef.current);
      sessionStatusHeartbeatIntervalRef.current = null;
    }
    participantTokenHeartbeatRef.current = '';
  }, []);

  /**
   * Gọi mvp/web-update-session-status (left) rồi dừng heartbeat.
   * @param {{ unloading?: boolean, clearPersisted?: boolean }} [opts]
   *   - unloading: dùng fetch keepalive (reload/đóng tab — axios thường bị abort).
   *   - clearPersisted: xóa token trong sessionStorage (mặc định true; pagehide giữ lại để same-tab reload reuse).
   */
  const notifyParticipantLeftThenClearHeartbeat = useCallback((opts) => {
    const options = opts || {};
    const clearPersisted = options.clearPersisted !== false;
    const tokenForLeft = participantTokenHeartbeatRef.current;
    const sessionUserIdForPersist = sessionUserIdRef.current;
    clearSessionStatusHeartbeat();
    if (clearPersisted && sessionUserIdForPersist) {
      clearPersistedParticipantToken(sessionUserIdForPersist);
    }
    if (!tokenForLeft) return;

    if (options.unloading) {
      releaseParticipantLockKeepalive(tokenForLeft);
      return;
    }
    if (typeof webUpdateSessionStatus === 'function') {
      Promise.resolve(
        webUpdateSessionStatus({ participantToken: tokenForLeft, status: 'left' }),
      ).catch(() => {});
    }
  }, [clearSessionStatusHeartbeat, webUpdateSessionStatus]);

  const shouldYieldToPeerMic = useCallback((users, cedUserId, myZoomUserId) => {
    const ignoreIds = preExistingSameUserMicOnIdsRef.current;
    return findSameCedUserParticipants(users, cedUserId, myZoomUserId).some(
      (participant) =>
        participant.muted === false && !ignoreIds.has(String(participant.userId)),
    );
  }, []);

  const muteWebAudio = useCallback(async (zoomClient) => {
    const activeClient = zoomClient || clientRef.current;
    if (!activeClient) return;
    try {
      const stream = activeClient.getMediaStream();
      await stream.muteAudio();
      if (!mountedRef.current) return;
      const currentUserId = activeClient.getCurrentUserInfo()?.userId;
      setIsAudioEnabled(false);
      isAudioEnabledRef.current = false;
      if (currentUserId != null) {
        setAllUsers((prevUsers) =>
          prevUsers.map((u) => (u.userId === currentUserId ? { ...u, muted: true } : u)),
        );
      }

      if (!hasShownMultiDeviceMicToastRef.current) {
        hasShownMultiDeviceMicToastRef.current = true;
        message.warning(I18n.t('multi_device_mic_auto_muted'));
      }
    } catch (e) {
      console.warn('Could not mute audio for multi-device:', e);
    }
  }, []);

  /** Live Zoom list: mute this device when a later same-user device has mic on. */
  const enforceMultiDeviceMicExclusivity = useCallback((users, zoomClient) => {
    const activeClient = zoomClient || clientRef.current;
    if (!activeClient || !isAudioEnabledRef.current) return;
    const myCedUserId = resolveMyCedUserId(user, activeClient.getCurrentUserInfo());
    if (!myCedUserId) return;
    const myZoomUserId = activeClient.getCurrentUserInfo()?.userId;
    if (shouldYieldToPeerMic(users, myCedUserId, myZoomUserId)) {
      muteWebAudio(activeClient);
    }
  }, [muteWebAudio, shouldYieldToPeerMic, user]);

  /** Peer cùng user vừa vào mic ON → web tắt. Peer mic OFF → giữ nguyên mic web (không auto-unmute). */
  const handleSameUserPeerAdded = useCallback(
    (users, payload, zoomClient) => {
      const activeClient = zoomClient || clientRef.current;
      if (!activeClient) return;
      const myCedUserId = resolveMyCedUserId(user, activeClient.getCurrentUserInfo());
      if (!myCedUserId) return;
      const myZoomUserId = activeClient.getCurrentUserInfo()?.userId;
      const added = normalizeParticipantEventPayload(payload);

      added.forEach((item) => {
        const full =
          (Array.isArray(users) &&
            users.find((u) => participantUserIdsMatch(u.userId, item.userId))) ||
          item;
        if (participantUserIdsMatch(full.userId, myZoomUserId)) return;
        if (getParticipantCedUserId(full) !== myCedUserId) return;

        if (full.muted === false) {
          muteWebAudio(activeClient);
        }
      });
    },
    [muteWebAudio, user],
  );

  const handleRailUserSelect = useCallback((targetUser) => {
    // Pins and sticky share own main layout; rail focus only when neither applies.
    if (pinnedDevicesRef.current.length > 0 || activeShareUserRef.current) return;
    if (allUsersRef.current.some((u) => u.sharerOn)) return;
    setRailFocusedUserId(String(targetUser.userId));
  }, []);

  const togglePin = useCallback((targetUser) => {
    const shareActive =
      !!activeShareUserRef.current || allUsersRef.current.some((u) => u.sharerOn);
    const maxPins = shareActive ? MAX_PINNED_WITH_SHARE : MAX_PINNED_DEVICES;
    setPinnedDevices((prev) => {
      const activeIds = new Set(allUsersRef.current.map((u) => String(u.userId)));
      const active = prev.filter((p) => activeIds.has(String(p.userId)));
      const isPinned = active.some((p) => participantUserIdsMatch(p.userId, targetUser.userId));
      if (isPinned) {
        return active.filter((p) => !participantUserIdsMatch(p.userId, targetUser.userId));
      }
      if (active.length >= maxPins) {
        message.warning(
          I18n.t(shareActive ? 'session_video_pin_max_one_with_share' : 'session_video_pin_max_two'),
        );
        return active;
      }
      return [...active, targetUser];
    });
  }, []);

  const joinSession = async () => {
    if (joiningInProgressRef.current || clientRef.current) return;
    joiningInProgressRef.current = true;
    setLoading(true);
    let notifyParticipantToken = '';
    let joinSessionUserId = '';
    try {
      const sessionInfo = await getDetailSession({ sessionId });
      if (!sessionInfo) throw new Error('No session info');
      joinSessionUserId =
        sessionInfo.sessionUserId != null ? String(sessionInfo.sessionUserId).trim() : '';
      if (joinSessionUserId) {
        sessionUserIdRef.current = joinSessionUserId;
      }

      let participantToken = '';
      const persistedToken = joinSessionUserId
        ? loadPersistedParticipantToken(joinSessionUserId)
        : '';

      // Same-tab reload: reuse persisted token + refresh ACTIVE lock (skip acquire).
      if (persistedToken && typeof webUpdateSessionStatus === 'function') {
        const probeRes = await webUpdateSessionStatus({
          participantToken: persistedToken,
          status: 'joined',
        });
        if (isWebUpdateSessionStatusSuccess(probeRes)) {
          participantToken = persistedToken;
        } else {
          clearPersistedParticipantToken(joinSessionUserId);
        }
      }

      if (!participantToken) {
        const lockData = await webAcquireZoomLock({
          sessionUserId: sessionInfo.sessionUserId,
        });
        if (!lockData || lockData.status !== 'OK') {
          const lockMsg =
            typeof lockData?.message === 'string' && lockData.message.trim()
              ? lockData.message.trim()
              : '';
          throw new Error(lockMsg || I18n.t('unable_join_session'));
        }

        const tokenFromLock = pickParticipantToken(lockData);
        notifyParticipantToken = tokenFromLock || '';
        participantToken = tokenFromLock || '';
      } else {
        notifyParticipantToken = participantToken;
      }

      const verifyData = await verifyJoinSession({
        sessionId: sessionInfo.sessionUserId,
        sessionUserId: sessionInfo.sessionUserId,
      });
      if (!verifyData || verifyData.status !== 'OK') {
        throw new Error(I18n.t('unable_join_session'));
      }

      if (!participantToken) {
        participantToken =
          pickParticipantToken(verifyData) ||
          (joinSessionUserId ? joinSessionUserId : '');
      }
      if (!participantToken) {
        throw new Error(I18n.t('unable_join_session'));
      }
      notifyParticipantToken = participantToken;

      let infoZoom = verifyData.infoZoom;
      if (typeof infoZoom === 'string') infoZoom = JSON.parse(infoZoom);
      if (!infoZoom) throw new Error(I18n.t('video_session_info_not_found'));

      const tokenData = await getTokenSession({ sessionName: infoZoom.sessionName });
      const token = typeof tokenData === 'string' ? tokenData : tokenData?.token || tokenData?.data;
      if (!token) throw new Error(I18n.t('unable_join_session'));
      // Đọc tên tại thời điểm join (không chỉ dựa Redux lúc mount — reload thường chưa có name).
      const userName = resolveZoomDisplayName(user) || I18n.t('anonymous_user');
      const sessionPasscode = infoZoom.password || '';

      const client = ZoomVideo.createClient();
      await client.init('en-US', 'Global');
      // ZoomVideo.createClient() trả về CÙNG một instance mỗi lần gọi (singleton theo tài liệu SDK),
      // nên client này có thể vẫn đang ở trong phiên trước. Phải rời phiên cũ TRƯỚC khi join —
      // và phải await: gọi leave() kiểu fire-and-forget rồi join ngay sẽ tạo race và Zoom trả
      // "duplicated operation". Client chưa từng join thì leave() reject, nuốt lỗi là đúng.
      try {
        await client.leave();
      } catch (leaveErr) {
        console.warn('[Join session] leave() trước khi join (bỏ qua, thường do chưa ở trong phiên nào):', leaveErr);
      }
      await client.join(infoZoom.sessionName, token, userName, sessionPasscode);
      if (!mountedRef.current) {
        try {
          await client.leave();
        } catch (e) {
          console.warn('Leave on unmount:', e);
        }
        return;
      }

      if (typeof webUpdateSessionStatus === 'function') {
        const statusRes = await webUpdateSessionStatus({ participantToken, status: 'joined' });
        if (!isWebUpdateSessionStatusSuccess(statusRes)) {
          const apiMsg =
            typeof statusRes?.message === 'string' && statusRes.message.trim()
              ? statusRes.message.trim()
              : I18n.t('unable_join_session');
          try {
            await client.leave();
          } catch (leaveErr) {
            console.warn('Leave after webUpdateSessionStatus failed:', leaveErr);
          }
          throw new Error(apiMsg);
        }
        if (!mountedRef.current) {
          try {
            await client.leave();
          } catch (leaveErr) {
            console.warn('Leave on unmount after joined status:', leaveErr);
          }
          return;
        }
        clearSessionStatusHeartbeat();
        participantTokenHeartbeatRef.current = participantToken;
        if (joinSessionUserId) {
          persistParticipantToken(joinSessionUserId, participantToken);
        }
        sessionStatusHeartbeatIntervalRef.current = window.setInterval(() => {
          const t = participantTokenHeartbeatRef.current;
          if (!t || typeof webUpdateSessionStatus !== 'function') return;
          Promise.resolve(webUpdateSessionStatus({ participantToken: t, status: 'joined' }))
            .then((res) => {
              console.log('[web-update-session-status interval]', res);
            })
            .catch(() => {});
        }, 60_000);
      }

      sessionUserIdRef.current = sessionInfo.sessionUserId;
      clientRef.current = client;
      setClient(client);

      if (DEBUG_SENSITIVE_KEYWORD) {
        const categoriesCount = sensitiveKeywordsConfig.categories?.length ?? 0;
        const keywordsCount = sensitiveKeywordsConfig.categories?.reduce(
          (sum, c) => sum + (c.keywords?.length ?? 0),
          0
        ) ?? 0;
        console.log('[Sensitive keyword] Cấu hình đã load:', {
          categoriesCount,
          keywordsCount,
          categories: sensitiveKeywordsConfig.categories?.map((c) => c.name) ?? [],
        });
        console.log(
          "[Live Transcript] Đã đăng ký listener 'caption-message'. Cần bật Live Transcription trong phiên Zoom để nhận transcript."
        );
      }
      // Một số bản SDK emit 'transcription-text' thay cho caption-message (debug only).
      if (DEBUG_SENSITIVE_KEYWORD && typeof client.on === 'function') {
        try {
          client.on('transcription-text', (p) => {
            console.log('[Live Transcript] [DEBUG] Event transcription-text nhận được:', p ? { keys: Object.keys(p), text: p.text ?? p.message ?? p.content } : p);
          });
        } catch (e) {
          // Event may be absent on this SDK build.
        }
      }

      const showSensitiveModal = (info) => {
        const modalState = {
          // null khi cảnh báo thuần L2. Trước đây fallback sang category rồi sang chuỗi
          // 'sensitive', khiến mọi bản ghi đều trông như bộ lọc từ khoá L1 có trúng.
          keyword: info.keyword || null,
          category: info.category || 'sensitive',
          speaker: info.speakerName || null,
          transcript: info.text,
          source: info.source,
          confidence: info.confidence != null ? info.confidence : null,
          // Thời điểm AI ra cảnh báo — chốt ở đây, KHÔNG phải lúc user bấm nút trên modal.
          // Nhánh end_session còn chờ user nhập feedback nên hai mốc lệch nhau rất xa.
          detectedAt: Date.now(),
        };
        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Sensitive keyword] Hiển thị modal kiểm duyệt.', {
            source: info.source,
            category: modalState.category,
            confidence: modalState.confidence,
          });
        }
        // Nội dung đã được đưa ra cảnh báo → xóa buffer để lần check sau không báo lại
        // trên chính đoạn hội thoại này khi user bấm "Tiếp tục".
        rollingSegmentsRef.current = [];
        pendingL1MatchRef.current = null;
        // Mốc tạm: trong lúc modal mở đã có guard riêng chặn, nhưng nhánh "Dừng cuộc gọi" đóng
        // modal này để mở modal feedback nên vẫn cần một mốc cooldown ở đó. Hai nhánh tiếp tục
        // phiên sẽ ghi đè mốc + độ dài theo đúng lựa chọn của user.
        lastAlertAtRef.current = Date.now();
        alertCooldownMsRef.current = ALERT_COOLDOWN_DEFAULT_MS;
        sensitiveModalOpenRef.current = true;
        try {
          // Lưu trạng thái mic/cam trước khi tạm dừng — để khôi phục đúng khi user đóng modal
          mediaStateBeforeSensitiveModalRef.current = {
            audio: isAudioEnabledRef.current,
            video: isVideoEnabledRef.current,
          };
          const stream = client.getMediaStream();
          const currentUserId = client.getCurrentUserInfo()?.userId;
          if (stream) {
            stream.muteAudio().catch(() => {});
            stream.stopVideo().catch(() => {});
            if (currentUserId) {
              try {
                stream.detachVideo(currentUserId);
              } catch (detachErr) {
                // ignore
              }
            }
          }
          setIsAudioEnabled(false);
          setIsVideoEnabled(false);
        } catch (e) {
          if (DEBUG_SENSITIVE_KEYWORD) console.warn('[Sensitive keyword] Không tạm dừng được media:', e);
        }
        setSensitiveModal(modalState);
      };

      /** Hoãn lần check sang thời điểm hết ràng buộc, thay vì vứt caption đi. */
      const scheduleSensitiveCheck = (speakerName, forceNow, delayMs) => {
        if (pendingL2TimerRef.current) clearTimeout(pendingL2TimerRef.current);
        pendingL2TimerRef.current = setTimeout(() => {
          pendingL2TimerRef.current = null;
          if (!mountedRef.current) return;
          runSensitiveCheck(speakerName, forceNow);
        }, Math.max(delayMs, 50));
      };

      /**
       * Gọi L2 trên rolling buffer và quyết định có cảnh báo hay không.
       * L1 KHÔNG còn tự quyết định — nó chỉ (a) ép bỏ qua debounce khi trúng từ khóa,
       * (b) làm fallback khi L2 không trả lời được (engineStatus != 'ok').
       */
      function runSensitiveCheck(speakerName, forceNow) {
        if (!mountedRef.current) return;
        if (sensitiveModalOpenRef.current) {
          if (DEBUG_SENSITIVE_KEYWORD) console.log('[Sensitive keyword] Bỏ qua: modal đang mở.');
          return;
        }
        const sinceAlert = Date.now() - lastAlertAtRef.current;
        const cooldownMs = alertCooldownMsRef.current;
        if (lastAlertAtRef.current && sinceAlert < cooldownMs) {
          if (DEBUG_SENSITIVE_KEYWORD) {
            console.log('[Sensitive keyword] Bỏ qua: còn cooldown', {
              cooldownMs,
              remainingMs: cooldownMs - sinceAlert,
            });
          }
          return;
        }
        const bufferText = buildRollingText(rollingSegmentsRef.current);
        if (!bufferText || !bufferText.trim()) return;

        if (sensitiveCheckInFlightRef.current) {
          // Caption vẫn nằm trong buffer nên không mất — chỉ cần thử lại sau.
          if (DEBUG_SENSITIVE_KEYWORD) console.log('[Sensitive keyword] Đang có request L2 chạy — hoãn lần check.');
          scheduleSensitiveCheck(speakerName, forceNow, L2_MIN_REQUEST_INTERVAL_MS);
          return;
        }
        const sinceLast = Date.now() - lastL2RequestAtRef.current;
        if (!forceNow && lastL2RequestAtRef.current && sinceLast < L2_MIN_REQUEST_INTERVAL_MS) {
          if (DEBUG_SENSITIVE_KEYWORD) console.log('[Sensitive keyword] Debounce — hoãn lần check.');
          scheduleSensitiveCheck(speakerName, forceNow, L2_MIN_REQUEST_INTERVAL_MS - sinceLast);
          return;
        }

        const l1Match = pendingL1MatchRef.current;
        sensitiveCheckInFlightRef.current = true;
        lastL2RequestAtRef.current = Date.now();
        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Sensitive keyword] Gọi API moderate-text (L2)...', {
            bufferLength: bufferText.length,
            preview: bufferText.substring(0, 80),
            l1Matched: !!l1Match,
            forceNow,
          });
        }

        const thunk = moderateTextAction && moderateTextAction({ text: bufferText });
        const promise = typeof thunk === 'function' ? thunk() : thunk;
        (promise || Promise.resolve(null))
          .then((res) => {
            if (!mountedRef.current) return;
            // Chuẩn hóa response: client có thể trả res hoặc res.data tùy cách wrap
            const payload = res && (res.data !== undefined ? res.data : res);
            // Không có payload → coi như L2 không trả lời được, KHÔNG coi là an toàn.
            const engineStatus = payload ? payload.engineStatus || 'ok' : 'error';
            const l2Available = engineStatus === 'ok';
            const l2Detected = !!(payload && payload.detected === true);

            if (DEBUG_SENSITIVE_KEYWORD) {
              console.log('[Sensitive keyword] Kết quả L2:', {
                engineStatus,
                detected: l2Detected,
                category: payload ? payload.category : null,
                confidence: payload ? payload.confidence : null,
              });
            }

            if (l2Available) {
              if (l2Detected) {
                showSensitiveModal({
                  keyword: (payload && payload.keyword) || (l1Match && l1Match.keyword) || null,
                  category: (payload && payload.category) || 'sensitive',
                  speakerName,
                  text: bufferText,
                  source: l1Match ? 'l1+l2' : 'l2',
                  confidence: payload ? payload.confidence : null,
                });
              } else {
                // L2 đã xét và kết luận an toàn → tin L2, kể cả khi L1 trúng từ khóa.
                // Đây chính là nhánh gỡ false positive cho các câu kiểu
                // "An OTP adds another layer of security" / "Keeping your CVV private is important".
                if (DEBUG_SENSITIVE_KEYWORD && l1Match) {
                  console.log('[Sensitive keyword] L1 trúng nhưng L2 kết luận an toàn → không cảnh báo.', {
                    keyword: l1Match.keyword,
                  });
                }
                pendingL1MatchRef.current = null;
              }
              return;
            }

            // L2 không dùng được (thiếu API key / lỗi mạng) → fallback về L1 để không mất lưới đỡ.
            if (l1Match) {
              if (DEBUG_SENSITIVE_KEYWORD) {
                console.warn('[Sensitive keyword] L2 không khả dụng → fallback cảnh báo theo L1.', {
                  engineStatus,
                  keyword: l1Match.keyword,
                });
              }
              showSensitiveModal({
                keyword: l1Match.keyword,
                category: l1Match.category,
                speakerName: l1Match.speakerName || speakerName,
                text: bufferText,
                source: 'l1-fallback',
                confidence: null,
              });
            }
          })
          .catch((err) => {
            if (!mountedRef.current) return;
            if (DEBUG_SENSITIVE_KEYWORD) {
              console.warn('[Sensitive keyword] Lỗi gọi moderate-text:', err && (err.message || err.response || err));
            }
            // Lỗi ở tầng client cũng là "L2 không trả lời được" → fallback L1.
            const l1Fallback = pendingL1MatchRef.current;
            if (l1Fallback) {
              showSensitiveModal({
                keyword: l1Fallback.keyword,
                category: l1Fallback.category,
                speakerName: l1Fallback.speakerName || speakerName,
                text: bufferText,
                source: 'l1-fallback',
                confidence: null,
              });
            }
          })
          .finally(() => {
            sensitiveCheckInFlightRef.current = false;
            if (DEBUG_SENSITIVE_KEYWORD) console.log('[Sensitive keyword] Xong xử lý caption.');
          });
      }

      const handleTranscriptPayload = (eventName, payload) => {
        const textRaw = payload && (payload.text ?? payload.message ?? payload.content ?? payload.transcript ?? payload.messageContent);
        const text = textRaw != null ? String(textRaw).trim() : '';
        // null khi SDK không cho biết ai nói: 'Unknown' là chuỗi để hiển thị, đừng ghi nó xuống
        // DB như một tên người thật.
        const speakerName = (payload && (payload.displayName || payload.userName || payload.speakerName || payload.speaker)) || null;
        // msgId định danh MỘT câu qua nhiều lần bắn caption tạm thời; done=true là câu đã nói xong.
        const msgId = (payload && (payload.msgId || payload.messageId)) || null;
        const isFinal = !payload || payload.done !== false;

        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Sensitive NLP] Caption nhận được:', {
            event: eventName,
            speaker: speakerName || '(unknown)',
            msgId,
            done: payload && payload.done,
            textLength: text.length,
            textPreview: text ? text.substring(0, 60) + (text.length > 60 ? '...' : '') : '(rỗng)',
          });
        }
        if (!text) return;

        upsertTranscriptSegment(rollingSegmentsRef.current, text, msgId);

        // L1 chỉ là tín hiệu phụ: đánh dấu để (a) bỏ qua debounce, (b) fallback nếu L2 chết.
        const jsonResult = detectSensitiveByJson(text);
        if (jsonResult.detected) {
          pendingL1MatchRef.current = {
            keyword: jsonResult.keyword,
            category: jsonResult.category,
            speakerName,
          };
          if (DEBUG_SENSITIVE_KEYWORD) {
            console.log('[Sensitive keyword] L1 trúng từ khóa (chờ L2 xác nhận):', {
              category: jsonResult.category,
              keyword: jsonResult.keyword,
            });
          }
        }

        // Caption tạm thời vẫn được gom vào buffer ở trên, nhưng chưa gọi L2: phân loại một câu
        // đang nói dở ("The code is") vừa tốn token vừa dễ sai. Chờ câu nói xong rồi mới xét.
        // SDK không gửi `done` thì coi như đã xong, để không bao giờ tắt hẳn việc kiểm duyệt.
        if (!isFinal) {
          if (DEBUG_SENSITIVE_KEYWORD) {
            console.log('[Sensitive keyword] Caption tạm thời — gom vào buffer, chưa gọi L2.');
          }
          return;
        }

        runSensitiveCheck(speakerName, jsonResult.detected);
      };

      client.on('caption-message', (payload) => handleTranscriptPayload('caption-message', payload));
      try {
        client.on('live-transcription-message-received', (payload) => handleTranscriptPayload('live-transcription-message-received', payload));
      } catch (e) {
        // Zoom SDK có thể không có event này
      }

      // Tự động thử bật Live Transcription khi bất kỳ ai (Explorer hoặc Guide) join phiên — ai vào trước cũng thử bật để kiểm duyệt từ khóa sớm (Zoom chỉ cho phép host bật; nếu không phải host sẽ lỗi 7300, log thân thiện)
      try {
        const hasLtt = !!(client.getLiveTranscriptionClient && typeof client.getLiveTranscriptionClient === 'function');
        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Live Transcript] getLiveTranscriptionClient:', hasLtt ? 'có' : 'không có (SDK có thể không hỗ trợ hoặc phiên bản cũ)');
        }
        const lttClient = hasLtt ? client.getLiveTranscriptionClient() : null;
        if (!lttClient) {
          if (DEBUG_SENSITIVE_KEYWORD) console.log('[Live Transcript] SDK không có getLiveTranscriptionClient — cần host bật transcription trong Zoom.');
        } else if (typeof lttClient.startLiveTranscription !== 'function') {
          if (DEBUG_SENSITIVE_KEYWORD) {
            console.log('[Live Transcript] startLiveTranscription không khả dụng. Các method có sẵn:', typeof lttClient === 'object' ? Object.keys(lttClient).filter((k) => typeof lttClient[k] === 'function') : []);
          }
        } else {
          if (DEBUG_SENSITIVE_KEYWORD) console.log('[Live Transcript] Đang thử bật Live Transcript (startLiveTranscription)...');
          const result = lttClient.startLiveTranscription();
          if (result && typeof result.then === 'function') {
            result
              .then(() => { if (DEBUG_SENSITIVE_KEYWORD) console.log('[Live Transcript] Đã bật Live Transcript thành công.'); })
              .catch((e) => {
                if (!DEBUG_SENSITIVE_KEYWORD) return;
                const code = e && (e.errorCode ?? e.code);
                const reason = (e && (e.reason || e.message)) || '';
                const is7300 =
                  code === 7300 ||
                  (e && e.type === 'INVALID_OPERATION') ||
                  (typeof reason === 'string' && reason.toLowerCase().includes('live transcription is not enabled'));
                if (is7300) {
                  console.log(
                    '[Live Transcript] Live Transcript chưa bật (có thể cần host bật). Bật add-on Live Transcription cho tài khoản Zoom hoặc để Guide (host) bật trong menu Zoom.'
                  );
                } else {
                  console.warn('[Live Transcript] Không bật được:', e);
                }
              });
          } else if (DEBUG_SENSITIVE_KEYWORD) {
            console.log('[Live Transcript] Đã gọi startLiveTranscription (sync).');
          }
        }
      } catch (e) {
        if (DEBUG_SENSITIVE_KEYWORD) console.warn('[Live Transcript] Lỗi khi bật:', e);
      }

      // Init chat client after join
      try {
        const chat = client.getChatClient();
        setChatClient(chat);

        // Optionally load existing chat history
        try {
          const history = await chat.getHistory();
          if (Array.isArray(history)) {
            const currentUserId = client.getCurrentUserInfo()?.userId;
            const mapped = history
              .filter((item) => item && (item.message || item.content))
              .map((item, idx) => ({
                id: item.id || `${item.timestamp || ''}-${idx}`,
                sender:
                  item.sender?.displayName ||
                  item.sender?.userName ||
                  item.sender?.userId ||
                  'Unknown',
                text: item.message || item.content || '',
                timestamp: item.timestamp || new Date().toISOString(),
                isSelf: item.sender?.userId === currentUserId,
              }));
            setChatMessages(mapped);
          }
        } catch (e) {
          console.warn('Error getting history:', e);
        }
      } catch (e) {
        console.warn('Chat client not available:', e);
      }

      // Start local media theo pref Pre-join (sessionStorage ced:prejoin:media:{sessionId})
      const stream = client.getMediaStream();
      const prejoinPrefs = loadPrejoinMediaPrefs(sessionId) || {
        audio: isAudioEnabledRef.current,
        video: isVideoEnabledRef.current,
        videoDeviceId: prejoinVideoDeviceIdRef.current || undefined,
      };
      clearPrejoinMediaPrefs(sessionId);

      const wantVideo = prejoinPrefs.video !== false;
      const wantAudio = prejoinPrefs.audio !== false;
      const preferredCameraId =
        typeof prejoinPrefs.videoDeviceId === 'string' && prejoinPrefs.videoDeviceId
          ? prejoinPrefs.videoDeviceId
          : '';

      setIsVideoEnabled(wantVideo);
      setIsAudioEnabled(wantAudio);
      isVideoEnabledRef.current = wantVideo;
      isAudioEnabledRef.current = wantAudio;

      let joinedWithAudioOn = false;
      const startJoinAudio = async () => {
        try {
          // Mic tắt: speakerOnly không nối micro nên không có nhịp mic bật lúc join.
          // Không startAudio() rồi muteAudio(): phát unmuted một nhịp, peer cùng user
          // đang bật mic sẽ thấy muted === false và tự tắt mic.
          if (!wantAudio) {
            try {
              await stream.startAudio({ mute: true, speakerOnly: true });
              audioSpeakerOnlyRef.current = true;
            } catch (speakerOnlyErr) {
              audioSpeakerOnlyRef.current = false;
              await stream.startAudio({ mute: true });
            }
            joinedWithAudioOn = false;
          } else {
            audioSpeakerOnlyRef.current = false;
            await stream.startAudio();
            joinedWithAudioOn = true;
          }
        } catch (e) {
          setIsAudioEnabled(false);
          isAudioEnabledRef.current = false;
          joinedWithAudioOn = false;
        }
      };

      // Mic tắt: vào audio trước video để không chờ camera mới tắt mic.
      if (!wantAudio) {
        await startJoinAudio();
      }

      if (wantVideo) {
        try {
          if (preferredCameraId) {
            try {
              await stream.startVideo({ cameraId: preferredCameraId });
            } catch (camErr) {
              await stream.startVideo();
              if (typeof stream.switchCamera === 'function') {
                try {
                  await stream.switchCamera(preferredCameraId);
                } catch (switchErr) {
                  console.warn('Could not switch to preferred camera:', switchErr);
                }
              }
            }
          } else {
            await stream.startVideo();
          }
        } catch (e) {
          setIsVideoEnabled(false);
          isVideoEnabledRef.current = false;
        }
      }

      if (wantAudio) {
        await startJoinAudio();
      }

      const getUsersWithLocalMicState = () =>
        applyPiPStashState(
          withSelfMutedWhenMicOff(
            client.getAllUser(),
            client.getCurrentUserInfo()?.userId,
            isAudioEnabledRef.current,
          ),
          pipStashedUserIdsRef.current,
        );

      const myZoomUserId = client.getCurrentUserInfo()?.userId;
      const users = getUsersWithLocalMicState();
      setAllUsers(users);

      const myCedUserIdForMic = resolveMyCedUserId(user, client.getCurrentUserInfo());
      if (joinedWithAudioOn && myCedUserIdForMic) {
        // Web vào sau: không yield vì peer đã mic ON sẵn; peer join sau sẽ không nằm trong set.
        preExistingSameUserMicOnIdsRef.current = new Set(
          findSameCedUserParticipants(users, myCedUserIdForMic, myZoomUserId)
            .filter((participant) => participant.muted === false)
            .map((participant) => String(participant.userId)),
        );
      } else {
        preExistingSameUserMicOnIdsRef.current = new Set();
      }

      users.forEach((user) => {
        if (user.sharerOn) {
          setActiveShareUser(user);
          // Chỉ reset renderType cho remote sharer. Local sharer đã do handleStartShare set
          // đúng ('video' trên Chromium / 'canvas' trên Safari) — nếu ép về 'canvas' sẽ mất
          // preview vì SDK đã bind vào <video>.
          if (user.userId !== myZoomUserId) {
            setShareRenderType('canvas');
          } else {
            const stream = client.getMediaStream();
            const renderType = stream.isStartShareScreenWithVideoElement() ? 'video' : 'canvas';
            setShareRenderType(renderType);
            pendingLocalShareViewRef.current = true;
          }
        }
      });

      client.on('user-added', (payload) => {
        // Last-joiner: peer cùng user mic ON → web tắt; mic OFF → giữ nguyên mic web.
        const nextUsers = getUsersWithLocalMicState();
        setAllUsers(nextUsers);
        handleSameUserPeerAdded(nextUsers, payload, client);
        enforceMultiDeviceMicExclusivity(nextUsers, client);
      });

      client.on('user-removed', (payload) => {
        const removed = normalizeParticipantEventPayload(payload);
        const removedIds = new Set(
          removed.map((item) => String(item.userId)).filter((id) => id !== 'undefined'),
        );

        if (removedIds.size > 0) {
          setPinnedDevices((prev) =>
            prev.filter((p) => !removedIds.has(String(p.userId))),
          );
        }
        removedIds.forEach((id) => pipStashedUserIdsRef.current.delete(id));

        removed.forEach((item) => {
          if (
            activeShareUserRef.current &&
            participantUserIdsMatch(activeShareUserRef.current.userId, item.userId)
          ) {
            setActiveShareUser(null);
            setShareRenderType('canvas');
            try {
              const stream = client.getMediaStream();
              stream.stopShareView();
            } catch (e) {
              console.warn('Could not stop share view:', e);
            }
          }
        });

        const users = getUsersWithLocalMicState();
        setAllUsers(users);
        const myCedUserId = resolveMyCedUserId(user, client.getCurrentUserInfo());
        const myZoomUserId = client.getCurrentUserInfo()?.userId;
        if (!shouldYieldToPeerMic(users, myCedUserId, myZoomUserId)) {
          hasShownMultiDeviceMicToastRef.current = false;
        }
      });

      client.on('user-updated', () => {
        const users = getUsersWithLocalMicState();
        setAllUsers((prev) => {
          if (!shouldUpdateParticipantsList(prev, users)) {
            return prev;
          }
          return users;
        });
        const sharer = findSharerFromUsers(users);
        if (sharer) {
          setActiveShareUser((prev) => (prev?.userId === sharer.userId ? prev : sharer));
          // Local sharer: giữ nguyên renderType đã set bởi handleStartShare.
          const myUserId = client.getCurrentUserInfo()?.userId;
          if (sharer.userId !== myUserId) {
            setShareRenderType('canvas');
          }
        } else if (activeShareUserRef.current) {
          setActiveShareUser(null);
          setShareRenderType('canvas');
          mediaRegistryRef.current.clearShareRegistry();
        }
        enforceMultiDeviceMicExclusivity(users, client);
      });

      // App iOS broadcast trạng thái PiP thu vào cạnh / kéo ra qua command channel.
      client.on('command-channel-message', (payload) => {
        const change = parsePiPStashCommand(payload);
        if (!change) return;
        const stashedIds = pipStashedUserIdsRef.current;
        if (stashedIds.has(change.senderId) === change.stashed) return;
        console.info(
          `[CED_WEB] PiP stash changed sender=${change.senderId} stashed=${change.stashed}`,
        );
        if (change.stashed) {
          stashedIds.add(change.senderId);
        } else {
          stashedIds.delete(change.senderId);
        }
        setAllUsers(getUsersWithLocalMicState());
      });

      client.on('passively-stop-share', () => {
        setActiveShareUser(null);
        setShareRenderType('canvas');
        pendingLocalShareRef.current = false;
        pendingLocalShareViewRef.current = false;
        mediaRegistryRef.current.clearShareRegistry();
        try {
          client.getMediaStream().stopShareView();
        } catch (e) {
          // ignore if view was not active
        }
      });

      client.on('connection-change', (payload) => {
        const { state, reason } = payload || {};
        if (state === 'Closed' || state === 'Fail') {
          notifyParticipantLeftThenClearHeartbeat();
          if (clientRef.current) {
            clientRef.current = null;
            setClient(null);
          }
          if (mountedRef.current) {
            if (reason === 'ended by host' || reason === 'kicked by host' || reason === 'expelled by host') {
              message.info(I18n.t('session_ended_by_sensitive_content'));
            } else if (reason) {
              message.info(I18n.t('session_connection_closed', { reason }));
            }
            history.push('/');
          }
        }
      });
    } catch (e) {
      clearSessionStatusHeartbeat();
      if (joinSessionUserId) {
        clearPersistedParticipantToken(joinSessionUserId);
      }
      if (notifyParticipantToken && typeof webUpdateSessionStatus === 'function') {
        Promise.resolve(
          webUpdateSessionStatus({ participantToken: notifyParticipantToken, status: 'failed' }),
        ).catch(() => {});
      }
      // Zoom Video SDK reject bằng object thuần { type, reason, errorCode }, KHÔNG phải Error.
      // Chỉ đọc e.message thì mất sạch thông tin, và history.push ngay sau đó xoá console —
      // nên log đầy đủ TRƯỚC khi điều hướng (bật "Preserve log" trong DevTools để đọc được).
      console.error('[joinSession] thất bại — raw:', e);
      try {
        console.error(
          '[joinSession] chi tiết:',
          JSON.stringify(
            e && typeof e === 'object'
              ? { type: e.type, reason: e.reason, errorCode: e.errorCode, name: e.name, message: e.message }
              : { value: String(e) },
          ),
        );
      } catch (logErr) {
        console.error('[joinSession] không serialize được error:', logErr);
      }
      if (mountedRef.current) {
        // reason là chỗ Zoom SDK để mô tả lỗi; ưu tiên nó khi không có message.
        message.error(e?.message || e?.reason || I18n.t('video_failed_join'));
        history.push('/');
      }
    } finally {
      joiningInProgressRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  };

  // Join once on mount; leave + clear heartbeat on unmount (avoids duplicate join).
  useEffect(() => {
    mountedRef.current = true;
    joinSession();
    return () => {
      mountedRef.current = false;
      joiningInProgressRef.current = false;
      // Reload/đóng tab: visibility thường đã hidden — giữ sessionStorage để remount reuse.
      // SPA navigate: vẫn visible — clear storage + axios left như bình thường.
      const pageUnloading =
        typeof document !== 'undefined' && document.visibilityState === 'hidden';
      notifyParticipantLeftThenClearHeartbeat({
        unloading: pageUnloading,
        clearPersisted: !pageUnloading,
      });
      if (clientRef.current) {
        clientRef.current.leave().catch(() => {});
        clientRef.current = null;
      }
      setClient(null);
    };
  }, []);

  // Reload / đóng tab: keepalive release Redis lock + fire-and-forget Zoom leave()
  // (axios thường bị abort; await leave() không kịp trên unload). Giữ sessionStorage để
  // same-tab reload reuse được nếu release chưa kịp hoàn tất trước khi remount.
  useEffect(() => {
    const onPageHide = (event) => {
      if (event && event.persisted) return;
      notifyParticipantLeftThenClearHeartbeat({ unloading: true, clearPersisted: false });
      // Best-effort: báo Zoom leave sớm để giảm bóng participant (~silent reconnect).
      // Không await — browser đang teardown; reject thì bỏ qua.
      const zoomClient = clientRef.current;
      if (zoomClient && typeof zoomClient.leave === 'function') {
        try {
          Promise.resolve(zoomClient.leave()).catch(() => {});
        } catch (_) {
          /* ignore sync throw */
        }
      }
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
    };
  }, [notifyParticipantLeftThenClearHeartbeat]);

  /** Luôn dừng heartbeat khi không còn client Zoom trong state (lớp an toàn, idempotent với các chỗ đã clear). */
  useEffect(() => {
    if (client == null) {
      clearSessionStatusHeartbeat();
    }
  }, [client, clearSessionStatusHeartbeat]);

  const leaveSession = async () => {
    if (client) {
      notifyParticipantLeftThenClearHeartbeat();
      await client.leave();
      clientRef.current = null;
      setClient(null);
      history.push(`/session-detail/${sessionId}`);
    }
  };

  const toggleAudio = async () => {
    if (!client) return;
    const stream = client.getMediaStream();
    const currentUserId = client.getCurrentUserInfo().userId;

    if (isAudioEnabled) {
      try {
        await stream.muteAudio();
        preExistingSameUserMicOnIdsRef.current = new Set();
        setIsAudioEnabled(false);
        isAudioEnabledRef.current = false;
        setAllUsers((prevUsers) =>
          prevUsers.map((user) => (user.userId === currentUserId ? { ...user, muted: true } : user)),
        );
      } catch (e) {
        console.warn('Could not mute audio:', e);
        message.warning(I18n.t('video_could_not_mute'));
      }
    } else {
      const myCedUserId = resolveMyCedUserId(user, client.getCurrentUserInfo());
      if (myCedUserId && hasSameUserWithMicOn(allUsers, myCedUserId, currentUserId)) {
        message.warning(I18n.t('multi_device_mic_blocked'));
        return;
      }
      try {
        if (audioSpeakerOnlyRef.current) {
          // speakerOnly chưa nối micro: unmuteAudio() không có tác dụng, phải vào lại audio có mic.
          await stream.stopAudio();
          audioSpeakerOnlyRef.current = false;
          try {
            await stream.startAudio();
          } catch (startErr) {
            try {
              await stream.startAudio({ mute: true, speakerOnly: true });
              audioSpeakerOnlyRef.current = true;
            } catch (restoreErr) {
              console.warn('Could not restore speaker-only audio:', restoreErr);
            }
            throw startErr;
          }
        } else {
          await stream.unmuteAudio();
        }
        hasShownMultiDeviceMicToastRef.current = false;
        setIsAudioEnabled(true);
        isAudioEnabledRef.current = true;
        setAllUsers((prevUsers) =>
          prevUsers.map((user) => (user.userId === currentUserId ? { ...user, muted: false } : user)),
        );
      } catch (e) {
        console.warn('Could not unmute audio:', e);
        message.warning(I18n.t('video_could_not_unmute'));
      }
    }
  };

  const startVideo = async (stream, userId) => {
    try {
      await stream.startVideo();
      // Attach vào ĐÚNG ô của user này. Trước đây dùng
      //   document.querySelector('video-player-container')
      // có hai lỗi: (1) trả về container ĐẦU TIÊN trong DOM — có thể là ô của người khác, nên
      // video của mình bị gắn nhầm chỗ cho tới khi useEffect dọn lại; (2) trả null khi chính
      // mình đang tắt cam, vì ô đó render có điều kiện theo bVideoOn → TypeError, và vì
      // startVideo re-throw nên nó chặn luôn phần code phía sau của hàm gọi.
      //
      // Ref có thể chưa tồn tại ngay lúc này (React chưa render lại sau khi bVideoOn đổi) —
      // không sao, useEffect [allUsers] sẽ attach bù qua startUserVideo().
      const container = userVideoRefs.current[userId];
      if (container) {
        const userVideo = await stream.attachVideo(userId, MAIN_RESOLUTION);
        container.innerHTML = '';
        container.appendChild(userVideo);
      }
    } catch (e) {
      console.warn('Could not start video:', e);
      message.warning(I18n.t('video_could_not_start_video'));
      throw e; // Re-throw to let toggleVideo handle it
    }
  };

  // Attach with registry dedupe so concurrent effects do not double-call attachVideo.
  const attachUserVideo = async (stream, userId, ref, isVideoOn, resolution) => {
    if (!stream || !userId || !ref) {
      return;
    }
    const registry = mediaRegistryRef.current;
    if (!registry.shouldReattachVideo(userId, ref, isVideoOn)) {
      return;
    }
    // Mark eagerly so concurrent effect runs don't both pass the dedupe check
    // and end up calling attachVideo twice for the same (userId, ref, isVideoOn).
    registry.markVideoAttached(userId, ref, isVideoOn);
    try {
      stream.detachVideo(userId);
    } catch (e) {
      // ignore nếu chưa từng attach
    }
    ref.innerHTML = '';
    if (!isVideoOn) {
      return;
    }
    try {
      const userVideo = await stream.attachVideo(userId, resolution);
      ref.appendChild(userVideo);
    } catch (e) {
      if (e && (e.errorCode === 6001 || (e.reason && e.reason.includes('not send video')))) {
        registry.markVideoAttached(userId, ref, false);
        return;
      }
      console.warn(`Could not attach video for user ${userId}:`, e);
    }
  };

  const reattachVisibleVideos = useCallback(
    async (users) => {
      if (!client) {
        return;
      }
      const stream = client.getMediaStream();
      for (const u of users) {
        const ref = userVideoRefs.current[u.userId];
        if (!ref) {
          continue;
        }
        const isVideoOn = !!(u.isVideoOn || u.bVideoOn);
        await attachUserVideo(stream, u.userId, ref, isVideoOn, getVideoResolutionForUser(u.userId));
      }
    },
    [client, getVideoResolutionForUser],
  );

  const stopVideo = async (stream, userId) => {
    try {
      await stream.stopVideo();
      stream.detachVideo(userId);
    } catch (e) {
      console.warn('Could not stop video:', e);
    }
  };

  // Bật/tắt video
  const toggleVideo = async () => {
    if (!client) return;
    const stream = client.getMediaStream();
    const userId = client.getCurrentUserInfo().userId;
    if (isVideoEnabled) {
      try {
        await stopVideo(stream, userId);
        setIsVideoEnabled(false);
      } catch (e) {
        console.warn('Could not stop video:', e);
        message.warning(I18n.t('video_could_not_stop_video'));
      }
    } else {
      try {
        await startVideo(stream, userId);
        setIsVideoEnabled(true);
      } catch (e) {
        console.warn('Could not start video:', e);
        // startVideo already surfaces a user message
      }
    }
  };

  /** Auto-mute web when a later same-user device has mic on (not ignored pre-existing peers). */
  useEffect(() => {
    if (!client || !isAudioEnabled) return;

    const myCedUserId = resolveMyCedUserId(user, client.getCurrentUserInfo());
    if (!myCedUserId) return;

    const myZoomUserId = client.getCurrentUserInfo()?.userId;
    if (shouldYieldToPeerMic(allUsers, myCedUserId, myZoomUserId)) {
      muteWebAudio(client);
    }
  }, [allUsers, client, isAudioEnabled, muteWebAudio, shouldYieldToPeerMic, user]);

  // Click: set pending + renderType + trim pin. SDK startShareScreen runs in
  // useLayoutEffect after the sticky slot is visible (preserves user-gesture).
  const handleStartShare = () => {
    if (!client) {
      return;
    }
    const currentUser = client.getCurrentUserInfo();
    const stream = client.getMediaStream();

    const useVideoElement = stream.isStartShareScreenWithVideoElement();
    const renderType = useVideoElement ? 'video' : 'canvas';

    pendingLocalShareRef.current = true;
    setShareRenderType(renderType);
    // Trim pins in the same click tick so layout settles before the SDK effect runs.
    setPinnedDevices((prev) =>
      prev.length > MAX_PINNED_WITH_SHARE ? prev.slice(0, MAX_PINNED_WITH_SHARE) : prev,
    );
    setActiveShareUser(currentUser);
  };

  const handleStopShare = async () => {
    if (!client) {
      return;
    }
    pendingLocalShareRef.current = false;
    pendingLocalShareViewRef.current = false;
    setActiveShareUser(null);
    setShareRenderType('canvas');
    mediaRegistryRef.current.clearShareRegistry();
    const stream = client.getMediaStream();
    try {
      stream.stopShareView();
    } catch (e) {
      // ignore if view was not active
    }
    try {
      await stream.stopShareScreen();
    } catch (e) {
      message.error(e.message || I18n.t('video_failed_stop_share'));
    }
  };

  useEffect(() => {
    if (!client) return;
    const stream = client.getMediaStream();
    const onActiveShareChange = (payload) => {
      const myUserId = client.getCurrentUserInfo()?.userId;
      if (payload.state === 'Active') {
        const shareUser = allUsersRef.current.find((u) => u.userId === payload.userId);
        setPinnedDevices((prev) =>
          prev.length > MAX_PINNED_WITH_SHARE ? prev.slice(0, MAX_PINNED_WITH_SHARE) : prev,
        );
        setActiveShareUser(shareUser || { userId: payload.userId });
        // Local sharer: handleStartShare đã set renderType. Chỉ reset về 'canvas' cho remote.
        if (payload.userId !== myUserId) {
          setShareRenderType('canvas');
        }
      } else if (payload.state === 'Inactive') {
        setActiveShareUser(null);
        setShareRenderType('canvas');
        mediaRegistryRef.current.clearShareRegistry();
        try {
          stream.stopShareView();
        } catch (e) {
          // ignore if view was not active
        }
      }
    };
    client.on('active-share-change', onActiveShareChange);
    return () => {
      client.off('active-share-change', onActiveShareChange);
    };
  }, [client]);

  const bindShareStream = useCallback(
    async (target, embedInPinPane) => {
      if (!client || !target || !effectiveShareUser) return;
      const registry = mediaRegistryRef.current;
      const renderType = shareRenderType;
      if (!registry.shouldRebindShare(effectiveShareUser.userId, target, embedInPinPane, renderType)) {
        return;
      }
      const stream = client.getMediaStream();
      try {
        const ready = await waitForShareSlotReady(
          target,
          mainShareSlotRef.current,
          SHARE_SLOT_POLL_MAX_FRAMES,
        );
        if (!ready) {
          console.warn('Could not bind share stream: share slot not ready');
          return;
        }
        const prevTarget = registry.getShareTargetEl();
        if (prevTarget && prevTarget !== target) {
          try {
            await stream.stopShareView();
          } catch (e) {
            // ignore if view was not active
          }
        }
        await stream.startShareView(target, effectiveShareUser.userId);
        registry.markShareBound(effectiveShareUser.userId, target, embedInPinPane, renderType);
      } catch (e) {
        console.warn('Could not bind share stream:', e);
      }
    },
    [client, effectiveShareUser, shareRenderType],
  );

  const handleShareCanvasRef = useCallback((el) => {
    shareCanvasRef.current = el;
  }, []);

  const handleShareVideoRef = useCallback((el) => {
    shareVideoRef.current = el;
  }, []);

  const handleMainShareSlotRef = useCallback((el) => {
    mainShareSlotRef.current = el;
  }, []);

  const currentUserId = client?.getCurrentUserInfo()?.userId;
  const isCurrentUserSharing = effectiveShareUser && effectiveShareUser.userId === currentUserId;
  const isAnyoneSharing = !!effectiveShareUser;
  const isOtherUserSharing = isAnyoneSharing && !isCurrentUserSharing;

  // Re-attach when pin layout or camera on/off changes. Synchronous in the effect
  // so tests' act() flushes before the next step; concurrent runs dedupe via mediaRegistry.
  useEffect(() => {
    if (!client) return;
    reattachVisibleVideos(allUsersRef.current);
  }, [client, mainDeviceKey, pinnedDeviceKey, videoFingerprint, reattachVisibleVideos]);

  // Remote sharer only: startShareView into the sticky slot.
  // Local sharer is owned by useLayoutEffect (wait for slot + startShareScreen).
  // Running both would race startShareScreen → SDK 6205, then catch clears state and
  // renderType falls back to canvas → next attempt hits 6003.
  useEffect(() => {
    if (!client || !effectiveShareUser) return undefined;
    const isLocalSharer = effectiveShareUser.userId === client.getCurrentUserInfo()?.userId;
    if (isLocalSharer) return undefined;
    const target = shareRenderType === 'video' ? shareVideoRef.current : shareCanvasRef.current;
    if (!target) return undefined;
      const timer = setTimeout(() => bindShareStream(target, true), 0);
    return () => clearTimeout(timer);
  }, [client, effectiveShareUser, shareRenderType, bindShareStream]);

  // Move canvas/video into the sticky main share slot (appendChild keeps DOM identity).
  // Local share: after move, wait until slot ready → startShareScreen (user-gesture) →
  // startShareView(self) for the local preview pipeline.
  useLayoutEffect(() => {
    const canvas = shareCanvasRef.current;
    const video = shareVideoRef.current;
    const host = shareHostRef.current;
    if (!host) return undefined;

    const slotTarget =
      effectiveShareUser && mainShareSlotRef.current ? mainShareSlotRef.current : host;

    if (canvas && canvas.parentNode !== slotTarget) slotTarget.appendChild(canvas);
    if (video && video.parentNode !== slotTarget) slotTarget.appendChild(video);

    if (!client || !effectiveShareUser) {
      return undefined;
    }

    const myUserId = client.getCurrentUserInfo()?.userId;
    const isLocalSharer = effectiveShareUser.userId === myUserId;
    const shouldStartLocalShare = pendingLocalShareRef.current && isLocalSharer;
    const shouldBindLocalShareView =
      pendingLocalShareViewRef.current && isLocalSharer && !pendingLocalShareRef.current;

    if (!shouldStartLocalShare && !shouldBindLocalShareView) {
      return undefined;
    }

    if (localShareInFlightRef.current) {
      return undefined;
    }

    let cancelled = false;

    const bindLocalShareView = async (stream, mediaTarget, sharerUserId) => {
      await stream.startShareView(mediaTarget, sharerUserId);
    };

    (async () => {
      localShareInFlightRef.current = true;
      const stream = client.getMediaStream();
      const renderType = shareRenderType;
      const mediaTarget = renderType === 'video' ? shareVideoRef.current : shareCanvasRef.current;
      const viewOnly = shouldBindLocalShareView && !shouldStartLocalShare;

      try {
        if (!mediaTarget) {
          pendingLocalShareRef.current = false;
          pendingLocalShareViewRef.current = false;
          return;
        }

        let ready = isShareSlotReady(mediaTarget, mainShareSlotRef.current);

        if (!ready) {
          ready = await waitForShareSlotReady(
            mediaTarget,
            mainShareSlotRef.current,
            SHARE_SLOT_POLL_FALLBACK_FRAMES,
          );
        }

        if (cancelled || !mountedRef.current) {
          return;
        }

        if (!ready) {
          pendingLocalShareRef.current = false;
          pendingLocalShareViewRef.current = false;
          if (!viewOnly) {
            mediaRegistryRef.current.clearShareRegistry();
            setActiveShareUser(null);
            setShareRenderType('canvas');
            message.error(I18n.t('video_could_not_prepare_share'));
          }
          return;
        }

        if (viewOnly) {
          pendingLocalShareViewRef.current = false;
          await bindLocalShareView(stream, mediaTarget, effectiveShareUser.userId);
          mediaRegistryRef.current.markShareBound(
            effectiveShareUser.userId,
            mediaTarget,
            true,
            renderType,
          );
          return;
        }

        await stream.startShareScreen(mediaTarget, { enableOptimizeForSharedVideo: true });
        pendingLocalShareRef.current = false;
        mediaRegistryRef.current.markShareBound(
          effectiveShareUser.userId,
          mediaTarget,
          true,
          renderType,
        );

        if (cancelled || !mountedRef.current) {
          return;
        }

        await bindLocalShareView(stream, mediaTarget, effectiveShareUser.userId);
      } catch (e) {
        pendingLocalShareRef.current = false;
        pendingLocalShareViewRef.current = false;
        mediaRegistryRef.current.clearShareRegistry();
        if (mountedRef.current && !viewOnly) {
          setActiveShareUser(null);
          setShareRenderType('canvas');
          message.error(e.message || I18n.t('video_failed_start_share'));
        }
      } finally {
        localShareInFlightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client, effectiveShareUser, mainDeviceKey, pinnedDeviceKey, shareRenderType]);

  // Trước khi SessionVideoCustom unmount, đưa canvas/video về hidden host để React
  // remove chúng đúng chỗ trong virtual DOM (tránh removeChild trên parent sai).
  useEffect(() => {
    return () => {
      const canvas = shareCanvasRef.current;
      const video = shareVideoRef.current;
      const host = shareHostRef.current;
      if (!host) return;
      try {
        if (canvas && canvas.parentNode !== host) host.appendChild(canvas);
        if (video && video.parentNode !== host) host.appendChild(video);
      } catch (e) {
        // ignore — component đang tear down
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (chatToastTimeoutRef.current) {
        clearTimeout(chatToastTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!client) return;

    const onChatMessage = (payload) => {
      try {
        const currentUserIdInner = client.getCurrentUserInfo()?.userId;
        const senderInfo = payload?.sender || {};
        const text = payload?.message || payload?.content || '';
        if (!text) return;

        const isSelf = senderInfo.userId === currentUserIdInner;
        const senderName = isSelf
          ? 'You'
          : senderInfo.name || senderInfo.userId || senderInfo.userGuid || 'Unknown';

        setChatMessages((prev) => [
          ...prev,
          {
            id: payload.id || payload.msgId || Date.now(),
            sender: senderName,
            text,
            timestamp: payload.timestamp || new Date().toISOString(),
            isSelf,
          },
        ]);

        // Toast only for others' messages while the chat panel is closed.
        if (!isSelf && !showChat) {
          if (chatToastTimeoutRef.current) {
            clearTimeout(chatToastTimeoutRef.current);
          }
          setChatToast({
            sender: senderName,
            text,
          });
          chatToastTimeoutRef.current = setTimeout(() => {
            setChatToast(null);
          }, 4000);
        }
      } catch (e) {
        console.warn('Error handling chat-on-message payload:', e);
      }
    };

    try {
      client.on('chat-on-message', onChatMessage);
    } catch (e) {
      console.warn('Failed to bind chat-on-message listener on client:', e);
    }

    return () => {
      try {
        client.off('chat-on-message', onChatMessage);
      } catch (e) {
        // ignore
      }
    };
  }, [client, showChat]);

  useEffect(() => {
    if (!showChat) return;
    const el = chatMessagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chatMessages, showChat]);

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;

    const text = chatInput.trim();
    setChatInput('');

    if (chatClient) {
      try {
        await chatClient.sendToAll(text);
      } catch (e) {
        console.warn('Failed to send chat via Zoom:', e);
        message.error(e.message || I18n.t('video_failed_send_message'));
      }
    }
  };

  /** Xử lý khi user chọn một trong 3 lựa chọn trên modal từ khóa nhạy cảm */
  /**
   * Trả mic/cam về đúng trạng thái ngay trước khi modal cảnh báo mở: đang bật thì bật lại,
   * đang tắt thì để nguyên tắt (không bao giờ tự bật thứ user đã chủ động tắt).
   *
   * Idempotent — tự xoá state đã lưu nên gọi nhiều lần không hại. Mọi lối thoát khỏi luồng cảnh
   * báo mà phiên vẫn tiếp tục đều PHẢI gọi hàm này: quên một lối là user kẹt ở trạng thái tắt
   * tiếng, và lần cảnh báo sau `showSensitiveModal` sẽ lưu chính trạng thái tắt đó làm "trạng
   * thái gốc" — từ đó mute thành vĩnh viễn.
   */
  const restoreMediaAfterSensitiveModal = async () => {
    const savedMedia = mediaStateBeforeSensitiveModalRef.current;
    mediaStateBeforeSensitiveModalRef.current = null;
    if (!client || !savedMedia) return;

    const stream = client.getMediaStream();
    const userId = client.getCurrentUserInfo()?.userId;
    if (!stream) return;

    // Audio và video khôi phục ĐỘC LẬP. Trước đây chung một try: video ném lỗi là nhánh audio
    // phía sau mất luôn, nên user thấy "cả mic lẫn cam đều không bật lại".
    try {
      if (savedMedia.audio) {
        await stream.unmuteAudio();
        setIsAudioEnabled(true);
      } else {
        if (!audioSpeakerOnlyRef.current) {
          await stream.muteAudio();
        }
        setIsAudioEnabled(false);
      }
    } catch (e) {
      console.warn('Could not restore audio after sensitive modal:', e);
      message.warning(I18n.t('video_resume_media_hint'));
    }

    if (!userId) return;
    try {
      if (savedMedia.video) {
        await startVideo(stream, userId);
        setIsVideoEnabled(true);
      } else {
        await stopVideo(stream, userId);
        setIsVideoEnabled(false);
      }
    } catch (e) {
      // startVideo() đã hiện toast riêng, ở đây chỉ log để không bắn 2 thông báo chồng nhau.
      console.warn('Could not restore video after sensitive modal:', e);
    }
  };

  /**
   * Huỷ ở modal "Dừng cuộc gọi" (nút Cancel và nút X) — phiên tiếp tục, nên khôi phục mic/cam.
   *
   * Vẫn ghi lại một bản ghi `END_SESSION_CANCELLED`: việc user định dừng cuộc gọi rồi đổi ý là
   * tín hiệu có thật về mức độ lo lắng của họ, khác hẳn với chọn "an toàn" ngay từ đầu. Backend
   * chỉ ghi vào historySensitiveData, không kết thúc phiên.
   */
  const handleEndSessionFeedbackCancel = async () => {
    const pending = pendingEndSessionRef.current;
    const sessionUserId = sessionUserIdRef.current;
    // Feedback đang gõ dở vẫn gửi kèm — user đã viết thì đừng vứt đi.
    const note = endSessionFeedbackNote.trim();

    setEndSessionFeedbackVisible(false);
    setEndSessionFeedbackNote('');
    pendingEndSessionRef.current = null;

    // Khôi phục mic/cam trước, không để việc log chặn user quay lại cuộc gọi.
    await restoreMediaAfterSensitiveModal();

    if (!pending || !sessionUserId) return;
    const params = {
      session_user_id: sessionUserId,
      speakerName: pending.speaker,
      keyword: pending.keyword,
      category: pending.category,
      type: 'END_SESSION_CANCELLED',
      transcript: pending.transcript || '',
      source: pending.source || 'unknown',
      confidence: pending.confidence != null ? pending.confidence : null,
      timestamp: pending.detectedAt || Date.now(),
      note: null,
    };
    if (note) params.note = note;
    try {
      const thunk = logSensitiveKeywordEventAction && logSensitiveKeywordEventAction(params);
      const promise = typeof thunk === 'function' ? thunk() : thunk;
      await (promise || Promise.resolve(null));
    } catch (e) {
      // Mất bản ghi này không được cản user tiếp tục phiên — chỉ log.
      console.warn('logSensitiveKeywordEvent (END_SESSION_CANCELLED) error:', e);
    }
  };

  const handleSensitiveModalChoice = async (type, note) => {
    if (!sensitiveModal || sensitiveModalSubmitting) return;
    const sessionUserId = sessionUserIdRef.current;
    if (!sessionUserId) {
      message.error(I18n.t('video_session_info_not_found'));
      return;
    }
    // end_session: open feedback modal; leave room + log happen on confirm.
    if (type === 'end_session') {
      pendingEndSessionRef.current = { ...sensitiveModal };
      setSensitiveModal(null);
      setSensitiveModalSubmitting(false);
      setEndSessionFeedbackVisible(true);
      setEndSessionFeedbackNote('');
      return;
    }

    setSensitiveModalSubmitting(true);
    const params = {
      session_user_id: sessionUserId,
      speakerName: sensitiveModal.speaker,
      keyword: sensitiveModal.keyword,
      category: sensitiveModal.category,
      type,
      transcript: sensitiveModal.transcript || '',
      source: sensitiveModal.source || 'unknown',
      // Gửi confidence của L2 để tune ngưỡng từ dữ liệu production (null khi cảnh báo đến từ L1 fallback).
      confidence: sensitiveModal.confidence != null ? sensitiveModal.confidence : null,
      // Thời điểm AI cảnh báo. Không gửi thì BE mặc định Date.now() lúc nhận request,
      // tức là ghi nhầm thành thời điểm user bấm nút.
      timestamp: sensitiveModal.detectedAt || Date.now(),
      note: null,
    };
    if (note !== undefined && note !== null) params.note = String(note).trim();
    try {
      const thunk = logSensitiveKeywordEventAction && logSensitiveKeywordEventAction(params);
      const promise = typeof thunk === 'function' ? thunk() : thunk;
      await (promise || Promise.resolve(null));
    } catch (e) {
      // API lỗi KHÔNG được giữ user lại trong modal ở trạng thái tắt tiếng: lựa chọn của họ là
      // "tiếp tục phiên". Chấp nhận mất bản ghi historySensitiveData lần này, chỉ báo lỗi.
      console.warn('logSensitiveKeywordEvent error:', e);
      message.error(I18n.t('video_failed_save_choice'));
    }
    // Cooldown tính từ đây — lúc user thực sự quay lại cuộc gọi — chứ không phải từ lúc cảnh báo
    // hiện ra: quãng user đọc modal là lúc micro đang tắt, không có gì để kiểm duyệt.
    // API log lỗi cũng không đổi lựa chọn của user, nên đặt sau catch.
    lastAlertAtRef.current = Date.now();
    alertCooldownMsRef.current =
      type === 'ACCIDENTAL_MENTION' ? ALERT_COOLDOWN_CAREFUL_MS : ALERT_COOLDOWN_SAFE_MS;
    if (DEBUG_SENSITIVE_KEYWORD) {
      console.log('[Sensitive keyword] Bắt đầu cooldown theo lựa chọn.', {
        choice: type,
        cooldownMs: alertCooldownMsRef.current,
      });
    }
    setSensitiveModal(null);
    setSensitiveModalSubmitting(false);

    // SAFE_CONFIRMED hoặc ACCIDENTAL_MENTION: tiếp tục phiên — khôi phục đúng trạng thái mic/cam trước khi mở modal
    await restoreMediaAfterSensitiveModal();
  };

  /** Xác nhận dừng cuộc gọi từ modal feedback (sau khi user đã chọn "Dừng cuộc gọi" trên modal chính). Luôn đóng modal và thoát cuộc gọi + chuyển trang trước, gọi API log sau để tránh API lỗi khiến user không dừng được. */
  const handleEndSessionFeedbackConfirm = async () => {
    const pending = pendingEndSessionRef.current;
    if (!pending) {
      setEndSessionFeedbackVisible(false);
      return;
    }
    const sessionUserId = sessionUserIdRef.current;
    if (!sessionUserId) {
      message.error(I18n.t('video_session_info_not_found'));
      setEndSessionFeedbackVisible(false);
      pendingEndSessionRef.current = null;
      return;
    }
    const params = {
      session_user_id: sessionUserId,
      speakerName: pending.speaker,
      keyword: pending.keyword,
      category: pending.category,
      type: 'end_session',
      transcript: pending.transcript || '',
      source: pending.source || 'unknown',
      confidence: pending.confidence != null ? pending.confidence : null,
      // Thời điểm AI cảnh báo, chốt từ lúc mở modal — user còn phải gõ feedback trước khi
      // request này được gửi, nên giờ server lệch đi hàng chục giây tới vài phút.
      timestamp: pending.detectedAt || Date.now(),
      note: null,
    };
    const note = endSessionFeedbackNote.trim();
    if (note) params.note = note;

    setSensitiveModalSubmitting(true);

    try {
      const thunk = logSensitiveKeywordEventAction && logSensitiveKeywordEventAction(params);
      const promise = typeof thunk === 'function' ? thunk() : thunk;
      const response = await (promise || Promise.resolve(null));

      if (!response || response.status !== 'OK') {
        throw new Error(I18n.t('video_failed_end_session'));
      }

      pendingEndSessionRef.current = null;
      setEndSessionFeedbackVisible(false);
      setEndSessionFeedbackNote('');
      setSensitiveModalSubmitting(false);

      notifyParticipantLeftThenClearHeartbeat();
      if (client) {
        try {
          await client.leave();
        } catch (e) {
          console.warn('Could not leave session:', e);
        }
        clientRef.current = null;
        setClient(null);
      }

      message.info(I18n.t('session_ended_by_sensitive_content'));
      history.push('/');
    } catch (e) {
      console.warn('logSensitiveKeywordEvent error:', e);
      message.error(I18n.t('session_end_failed'));
      setSensitiveModalSubmitting(false);
    }
  };

  const sensitiveModalOpen = !!sensitiveModal;

  return (
    <div className="session-video-wrapper">
      {/* Sensitive-keyword moderation modal */}
      <Modal
        title={I18n.t('video_call_sensitive_warning_title')}
        visible={sensitiveModalOpen}
        closable={false}
        maskClosable={false}
        footer={null}
        width={560}
        className="sensitive-keyword-modal"
        wrapClassName="sensitive-keyword-modal-wrap"
        getContainer={() => document.body}
        zIndex={2147483647}
      >
        {sensitiveModal && (
          <>
            <div className="sensitive-keyword-modal-body">
              <p className="sensitive-keyword-modal-content">
                {I18n.t('video_call_sensitive_warning_content')}
              </p>
              <div className="sensitive-keyword-infographic">
                <img
                  className="sensitive-keyword-infographic-image"
                  src="/UC2-Infographic-16_9.png"
                  alt={I18n.t('video_call_sensitive_guide_link')}
                />
              </div>
            </div>
            <div className="sensitive-keyword-modal-footer">
              <Button
                type="primary"
                block
                className="sensitive-keyword-btn safe"
                loading={sensitiveModalSubmitting}
                onClick={() => handleSensitiveModalChoice('SAFE_CONFIRMED')}
              >
                <span className="sensitive-keyword-btn-title">{I18n.t('video_call_sensitive_action_continue_safe')}</span>
                <span className="sensitive-keyword-btn-subtitle">{I18n.t('video_call_sensitive_action_continue_safe_subtitle')}</span>
              </Button>
              <Button
                block
                className="sensitive-keyword-btn accidental"
                loading={sensitiveModalSubmitting}
                onClick={() => handleSensitiveModalChoice('ACCIDENTAL_MENTION')}
              >
                <span className="sensitive-keyword-btn-title">{I18n.t('video_call_sensitive_action_continue_careful')}</span>
                <span className="sensitive-keyword-btn-subtitle">{I18n.t('video_call_sensitive_action_continue_careful_subtitle')}</span>
              </Button>
              <Button
                danger
                block
                className="sensitive-keyword-btn end"
                loading={sensitiveModalSubmitting}
                onClick={() => handleSensitiveModalChoice('end_session')}
              >
                <span className="sensitive-keyword-btn-title">{I18n.t('video_call_sensitive_action_stop_call')}</span>
                <span className="sensitive-keyword-btn-subtitle">{I18n.t('video_call_sensitive_action_stop_call_subtitle')}</span>
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* End-session confirmation + optional feedback note */}
      <Modal
        title={I18n.t('sensitive_session_negative_dialog_title')}
        visible={endSessionFeedbackVisible}
        closable={true}
        maskClosable={false}
        footer={null}
        width={400}
        className="sensitive-keyword-end-feedback-modal"
        wrapClassName="sensitive-keyword-modal-wrap"
        getContainer={() => document.body}
        zIndex={2147483646}
        onCancel={handleEndSessionFeedbackCancel}
      >
        <p className="sensitive-keyword-feedback-description">
          {I18n.t('sensitive_session_negative_dialog_description')}
        </p>
        <Input.TextArea
          placeholder={I18n.t('sensitive_session_negative_dialog_feedback_hint')}
          value={endSessionFeedbackNote}
          onChange={(e) => setEndSessionFeedbackNote(e.target.value)}
          rows={3}
          className="sensitive-keyword-feedback-input"
        />
        <div className="sensitive-keyword-feedback-footer">
          <Button onClick={handleEndSessionFeedbackCancel}>
            {I18n.t('cancel')}
          </Button>
          <Button
            type="primary"
            danger
            loading={sensitiveModalSubmitting}
            onClick={handleEndSessionFeedbackConfirm}
          >
            {I18n.t('sensitive_session_negative_dialog_end_call_button')}
          </Button>
        </div>
      </Modal>

      <div className={`session-video-main-layout${mainContent ? ' media-active' : ''}`}>
        <div className="session-video-main-section has-main">
          {!mainContent && <div className="session-video-main-placeholder" aria-hidden="true" />}
          {stickyShareOnMain &&
            mainUsers.length === 0 &&
            effectiveShareUser && (
              <SessionVideoUserBox
                key={`share-${effectiveShareUser.userId}`}
                user={effectiveShareUser}
                isMain
                isSharing
                hidePin
                currentUserId={currentUserId}
                onAssignShareSlot={handleMainShareSlotRef}
              />
            )}
          {stickyShareOnMain && mainUsers.length === 1 && effectiveShareUser && (
            <div className="session-video-dual-pin">
              <SessionVideoUserBox
                key={`share-${effectiveShareUser.userId}`}
                user={effectiveShareUser}
                isMain
                layoutVariant="half"
                isSharing
                hidePin
                currentUserId={currentUserId}
                onAssignShareSlot={handleMainShareSlotRef}
              />
              {(() => {
                const deviceUser = mainUsers[0];
                return (
                  <SessionVideoUserBox
                    key={deviceUser.userId}
                    user={deviceUser}
                    isMain
                    layoutVariant="half"
                    isPinned
                    currentUserId={currentUserId}
                    onTogglePin={togglePin}
                    onAssignVideoRef={assignUserVideoRef}
                  />
                );
              })()}
            </div>
          )}
          {!stickyShareOnMain &&
            mainUsers.length === 1 &&
            (() => {
              const mainUser = mainUsers[0];
              const isPinned = pinnedUserIds.has(String(mainUser.userId));
              return mainUser ? (
                <SessionVideoUserBox
                  key={mainUser.userId}
                  user={mainUser}
                  isMain
                  isPinned={isPinned}
                  currentUserId={currentUserId}
                  onTogglePin={togglePin}
                  onAssignVideoRef={assignUserVideoRef}
                />
              ) : null;
            })()}
          {!stickyShareOnMain && mainUsers.length === 2 && (
            <div className="session-video-dual-pin">
              {mainUsers.map((mainUser) => {
                const isPinned = pinnedUserIds.has(String(mainUser.userId));
                return mainUser ? (
                  <SessionVideoUserBox
                    key={mainUser.userId}
                    user={mainUser}
                    isMain
                    layoutVariant="half"
                    isPinned={isPinned}
                    currentUserId={currentUserId}
                    onTogglePin={togglePin}
                    onAssignVideoRef={assignUserVideoRef}
                  />
                ) : null;
              })}
            </div>
          )}
        </div>
        <div className="session-video-rail-section">
          <div
            className={`session-video-rail ${hasPin ? 'has-pin' : 'no-pin'} ${
              stickyShareOnMain ? 'has-share' : 'no-share'
            }`}
          >
            <div className="session-video-rail-header">{I18n.t('meeting_participants')} ({allUsers.length})</div>
            {allUsers.map((railUser) => {
              const inMain = mainUserIds.has(String(railUser.userId));
              const railSelectable = !hasPin && !stickyShareOnMain;
              return (
                <SessionVideoUserBox
                  key={railUser.userId}
                  user={railUser}
                  isRail
                  isRailSelectable={railSelectable}
                  isInMain={inMain}
                  isPinned={pinnedUserIds.has(String(railUser.userId))}
                  currentUserId={currentUserId}
                  onTogglePin={togglePin}
                  onRailSelect={railSelectable ? handleRailUserSelect : undefined}
                  onAssignVideoRef={inMain ? undefined : assignUserVideoRef}
                />
              );
            })}
          </div>
        </div>
      </div>

      {showChat && (
        <div className="chat-panel">
          <div className="chat-header">
            <h5>{I18n.t('video_chat_title')}</h5>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setShowChat(false)}
              className="close-button"
            />
          </div>
          <div className="chat-messages" ref={chatMessagesRef}>
            {chatMessages.length === 0 && (
              <div className="chat-empty">{I18n.t('video_chat_empty')}</div>
            )}
            {chatMessages.map((item, index) => {
              const prev = chatMessages[index - 1];
              const showSender = !prev || prev.sender !== item.sender || prev.isSelf !== item.isSelf;

              return (
                <div
                  key={item.id}
                  className={`chat-message ${item.isSelf ? 'self' : 'other'}`}
                >
                  {showSender && (
                    <div className="chat-message-meta">
                      <span className="chat-sender">{item.sender}</span>
                    </div>
                  )}
                  <div className="chat-message-text">{item.text}</div>
                </div>
              );
            })}
          </div>
          <div className="chat-input-wrapper">
            <Input.TextArea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder={I18n.t('video_chat_placeholder')}
              autoSize={{ minRows: 1, maxRows: 3 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSendChat();
                }
              }}
            />
            <Button type="primary" onClick={handleSendChat} disabled={!chatInput.trim()} icon={<SendOutlined />} />
          </div>
        </div>
      )}

      <div className="session-video-controls-bar">
        <Tooltip title={isAudioEnabled ? I18n.t('video_mute_audio') : I18n.t('video_unmute_audio')}>
          <Button
            onClick={toggleAudio}
            style={{ marginRight: 8 }}
            icon={
              <span className="media-toggle-icon">
                <AudioOutlined />
                {!isAudioEnabled && <span className="media-toggle-slash" />}
              </span>
            }
            className="media-toggle-btn audio-toggle-btn"
          />
        </Tooltip>
        <Tooltip title={isVideoEnabled ? I18n.t('video_stop_video') : I18n.t('video_start_video')}>
          <Button
            onClick={toggleVideo}
            style={{ marginRight: 8 }}
            icon={
              <span className="media-toggle-icon">
                <VideoCameraOutlined />
                {!isVideoEnabled && <span className="media-toggle-slash" />}
              </span>
            }
            className="media-toggle-btn video-toggle-btn"
          />
        </Tooltip>
        {!isAnyoneSharing && (
          <Tooltip title={I18n.t('video_share_screen')}>
            <Button onClick={handleStartShare} style={{ marginRight: 8 }} icon={<ShareAltOutlined />} />
          </Tooltip>
        )}
        {isCurrentUserSharing && (
          <Tooltip title={I18n.t('video_stop_sharing')}>
            <Button onClick={handleStopShare} style={{ marginRight: 8 }} icon={<StopOutlined />} danger />
          </Tooltip>
        )}
        {isOtherUserSharing && (
          <Tooltip title={I18n.t('video_sharing_in_progress')} placement="top">
            <Button style={{ marginRight: 8 }} icon={<ShareAltOutlined />} disabled />
          </Tooltip>
        )}
        <Tooltip title={showChat ? I18n.t('video_close_chat') : I18n.t('video_open_chat')}>
          <Button
            onClick={() => setShowChat((prev) => !prev)}
            style={{ marginRight: 8 }}
            icon={<MessageOutlined />}
            type={showChat ? 'primary' : 'default'}
          />
        </Tooltip>
        <Tooltip title={I18n.t('video_leave_session')}>
          <Button danger onClick={leaveSession} icon={<PhoneOutlined />} />
        </Tooltip>
      </div>
      {chatToast && (
        <div
          className="chat-toast"
          onClick={() => {
            setShowChat(true);
            setChatToast(null);
          }}
        >
          <div className="chat-toast-sender">{chatToast.sender}</div>
          <div className="chat-toast-text">{chatToast.text}</div>
        </div>
      )}
      {loading && (
        <div className="session-video-loading-overlay">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      )}
      {/*
        Hidden host cho canvas + video của share stream. Luôn được render tại đây (không unmount)
        để giữ DOM identity ổn định — canvas/video được appendChild sang slot đích qua useLayoutEffect
        khi layout đổi, tránh việc React unmount/remount canvas làm Zoom SDK render vào element đã detached.
      */}
      <div ref={shareHostRef} className="session-video-share-host" aria-hidden="true">
        <canvas
          ref={handleShareCanvasRef}
          id="users-screen-share-content-canvas"
          width={1920}
          height={1080}
          className={`session-video-share-canvas${
            shareRenderType === 'canvas' && effectiveShareUser ? ' show' : ''
          }`}
        />
        <video
          ref={handleShareVideoRef}
          id="my-screen-share-content-video"
          width={960}
          height={540}
          className={`session-video-share-video${
            shareRenderType === 'video' && effectiveShareUser ? ' show' : ''
          }`}
          autoPlay
          playsInline
          muted
        />
      </div>
    </div>
  );
}

const mapStateToProps = ({ user }) => ({ user });
const mapDispatchToProps = {
  getDetailSession,
  getTokenSession,
  fetchUserMvp,
  verifyJoinSession,
  webAcquireZoomLock,
  webUpdateSessionStatus,
  moderateText,
  logSensitiveKeywordEvent,
};
export default connect(mapStateToProps, mapDispatchToProps)(SessionVideoCustom);
